<template>
  <CustomDropdown 
    maxHeight="300px" 
    gap="15px" 
    :position="position"
    :onHover="onHover"
    :width="asInputField ? 'w-full' : undefined"
    @open="handleDropdownOpen"
  >
    <template #trigger>
      <div 
        class="flex flex-row items-center cursor-pointer text-sm"
        :class="{
          'w-full px-3 py-2 rounded-md border justify-between min-h-9 input-field-border input-field-bg': asInputField
        }"
      >
        <ThemeImage
          v-if="!asInputField"
          class="mr-1"
          :icon="imageNames.CURLY_BRACE_GREY"
          alt="Curly Brace"
          loading="lazy"
        />
        <span
          v-if="!isSubjectField && !iconOnly"
          :class="[
            asInputField ? 'flex-1 text-left' : '',
            asInputField && !selectedVariableName ? 'secondary_color' : 'text_color'
          ]"
        >
          {{ displayText }}
        </span>
        <ThemeImage
          v-if="!isSubjectField && !iconOnly && buttonText && !isLimitedOptions"
          :icon="imageNames.DROPDOWN_GREY"
          alt="Drop Arrow"
          :class="asInputField ? 'size-4' : 'size-4 ml-1'"
        />
      </div>
    </template>

    <template #default>
      <div class="flex flex-col">
        <div class="flex-none">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row gap-1" v-if="!isLimitedOptions">
              <div
                v-for="tab in tabs"
                :key="tab.id"
                class="personalize_category cursor-pointer text-sm transition-all duration-300 ease-in-out"
                :class="{ active: activeTab === tab.id }"
                @click="setActiveTab(tab.id)"
              >
                {{ tab.name }}
              </div>
            </div>

            <CustomInput
              v-if="!isLimitedOptions"
              ref="searchInputRef"
              format="text"
              v-model="searchQuery"
              placeholder="Search here"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              size="size-4"
              alt="search_icon"
            />

            <div class="separator" v-if="!isLimitedOptions"></div>
          </div>
        </div>

        <div class="h-[200px] overflow-y-auto">
          <div class="py-2">
            <div 
              v-for="option in displayVariables" 
              :key="option.id"
              class="flex items-center flex-row gap-2 px-3 py-2 rounded-lg cursor-pointer mb-[2px] transition-colors personalize_options"
              @click="applyPersonalization(option)"
            >
              <div class="text-sm text_color">{{ option.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import {
  peopleVariables, 
  senderVariables, 
  companyVariables, 
  personalizationTabs,
  transformCustomPropertiesToVariables,
  limitedOptionsFilters
} from "@/src/constants/emailConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";

const props = defineProps({
  buttonText: {
    type: String,
  },
  position: {
    type: String,
    default: "topend"
  },
  isSubjectField: {
    type: Boolean,
    default: false
  },
  onHover: {
    type: Boolean,
    default: false
  },
  isLimitedOptions: {
    type: Boolean,
    default: false
  },
  asInputField: {
    type: Boolean,
    default: false
  },
  selectedVariableName: {
    type: String,
    default: null
  },
  supportsCustomProperties: {
    type: Boolean,
    default: true
  },
  iconOnly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['apply-personalization']);

const searchQuery = ref('');
const activeTab = ref('all');
const searchInputRef = ref(null);
const isClickThrottled = ref(false);
const isLoadingCustomProperties = ref(false);
const hasLoadedCustomProperties = ref(false);

const mergedPeopleVariables = ref([...peopleVariables.value]);
const mergedCompanyVariables = ref([...companyVariables.value]);
const mergedSenderVariables = ref([...senderVariables.value]);
const allMergedVariables = ref([
  ...peopleVariables.value,
  ...senderVariables.value,
  ...companyVariables.value,
]);

const tabs = personalizationTabs;

const categoryVariablesMap = {
  all: allMergedVariables,
  people: mergedPeopleVariables,
  company: mergedCompanyVariables,
  sender: mergedSenderVariables,
};

const displayText = computed(() => {
  return props.selectedVariableName || props.buttonText || "Personalize";
});

const filteredVariables = computed(() => {
  const variables = categoryVariablesMap[activeTab.value]?.value || [];
  if (!searchQuery.value) return variables;
  
  const lowerCaseQuery = searchQuery.value.toLowerCase();
  return variables.filter(variable => 
    variable.name?.toLowerCase().includes(lowerCaseQuery)
  );
});


const displayVariables = computed(() => {
  if (props.isLimitedOptions) {
    return limitedOptionsFilters.value;
  }
  return filteredVariables.value;
});

const setActiveTab = (tabId) => {
  activeTab.value = tabId;
  searchQuery.value = '';
};

const applyPersonalization = (option) => {
  if (isClickThrottled.value) {
    return;
  }
  
  isClickThrottled.value = true;
  emit('apply-personalization', option.tag, option);
  
  // Reset throttle after delay
  setTimeout(() => {
    isClickThrottled.value = false;
  }, 100);
};

const handleDropdownOpen = async () => {
  nextTick(() => {
    searchInputRef.value?.focus();
  });

  if (!hasLoadedCustomProperties.value && !isLoadingCustomProperties.value) {
    await loadCustomPropertiesData();
  }
};

const loadCustomPropertiesData = async () => {
  if (!props.supportsCustomProperties) return;
  if (isLoadingCustomProperties.value) return;
  
  try {
    isLoadingCustomProperties.value = true;
    
    const [contactsResponse, leadsResponse, companiesResponse] = await Promise.all([
      DashboardService.fetchCustomProperties('contact'),
      DashboardService.fetchCustomProperties('lead'), 
      DashboardService.fetchCustomProperties('company')
    ]);
    
    const contacts = contactsResponse.success ? 
      (contactsResponse.custom_properties || []).filter(property => 
        property.datatype !== "user"
      ) : [];
    const leads = leadsResponse.success ? 
      (leadsResponse.custom_properties || []).filter(property => 
        property.datatype !== "user"
      ) : [];
    const companies = companiesResponse.success ? 
      (companiesResponse.custom_properties || []).filter(property => 
        property.datatype !== "user"
      ) : [];
    
    const contactCustomVariables = transformCustomPropertiesToVariables(contacts, "people", "contact");
    const leadCustomVariables = transformCustomPropertiesToVariables(leads, "people", "contact");
    
    const combinedPeopleCustom = [...contactCustomVariables, ...leadCustomVariables];
    const uniquePeopleCustom = combinedPeopleCustom.reduce((unique, current) => {
      const existing = unique.find(item => item.slug === current.slug);
      if (!existing) {
        unique.push(current);
      }
      return unique;
    }, []);
    
    const companyCustomVariables = transformCustomPropertiesToVariables(companies, "company", "company");

    mergedPeopleVariables.value = [...peopleVariables.value, ...uniquePeopleCustom];
    mergedCompanyVariables.value = [...companyVariables.value, ...companyCustomVariables];
    mergedSenderVariables.value = [...senderVariables.value];
    
    allMergedVariables.value = [
      ...mergedPeopleVariables.value,
      ...mergedSenderVariables.value,
      ...mergedCompanyVariables.value,
    ];
    
    hasLoadedCustomProperties.value = true;
    
  } catch (error) {
    console.error('Error loading custom properties:', error);
  } finally {
    isLoadingCustomProperties.value = false;
  }
};

onMounted(async () => {
  await loadCustomPropertiesData();
});
</script>

<style scoped>
.personalize_category {
  color: var(--secondary-color);
  padding: 6px 8px;
  border-radius: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  line-height: 17px;
}

.personalize_category:hover {
  background-color: var(--white-opacity-6);
}

.personalize_category.active {
  background-color: var(--white-opacity-6);
  color: var(--text-color);
}

.personalize_options:hover {
  background-color: var(--white-opacity-8);
}

.separator {
  height: 1px;
  background-color: var(--white-opacity-8);
  margin: 0 -8px;
  width: calc(100% + 16px);
}

.input-field-border {
  border-color: var(--white-opacity-8);
  transition: border-color 0.2s;
}

.input-field-border:hover {
  border-color: var(--white-opacity-12);
}

.input-field-border:active {
  border-color: var(--white-opacity-16);
}

.input-field-bg {
  background: var(--white-opacity-2);
}

:deep(.dropdown) {
  transform: v-bind('isSubjectField ? "translateX(-228px)" : "none"') !important;
}

:deep(.dropdown.top),
:deep(.dropdown.topstart),
:deep(.dropdown.topend) {
  transform: v-bind('isSubjectField ? "translateY(0) translateX(-228px)" : "translateY(0)"') !important;
}

:deep(.dropdown.top.fade_out),
:deep(.dropdown.topstart.fade_out),
:deep(.dropdown.topend.fade_out) {
  transform: v-bind('isSubjectField ? "translateY(0) translateX(-228px)" : "translateY(0)"') !important;
}
</style> 